import { MidTradeEventAction, MidTradeEventSpreadsheetParseResult, ValidMidTradeEventAction } from './parseMidTradeEventSpreadsheet'
import { DealDetail, HaircutSign, MidTradeAction, MidTradeResponse, ServicingType, UserPreference, ValidationIssue } from 'api'
import { createInvalidValueValidationIssue } from 'model'
import { isNil } from 'lodash-es'
import { Locale, Translation } from 'locale'
import { mod } from 'utils/Decimal'

export enum PendingMidTradeEventFields {
  ACTION = 'ACTION',
  RESPONSE = 'RESPONSE',
  TRADE_ID = 'TRADE_ID',
  EFFECTIVE_DATE = 'EFFECTIVE_DATE',
  NOTIONAL = 'NOTIONAL',
  RATE = 'RATE',
  HAIRCUT = 'HAIRCUT',
  HAIRCUT_TYPE = 'HAIRCUT_TYPE'
}

type MidTradeValidateResult = {
  validMidTradeActions: ValidMidTradeEventAction[]
  invalidMidTradeActions: MidTradeEventAction[]
}

export const validateMidTradeEventParseResult = (
  parseResult: MidTradeEventSpreadsheetParseResult,
  incoming: boolean,
  userPreference: UserPreference,
  getDealDetail: (dealId: string) => DealDetail | null
): MidTradeValidateResult => {
  const { midTradeEventActions } = parseResult

  if (midTradeEventActions.length === 0) {
    return {
      validMidTradeActions: [],
      invalidMidTradeActions: []
    }
  }

  const allTradeIds = midTradeEventActions.map(midTradeEventAction => midTradeEventAction.tradeId)

  return midTradeEventActions.reduce<MidTradeValidateResult>(
    (actions, midTradeEventAction) => {
      const dataIssues: ValidationIssue[] = []

      if (allTradeIds.filter(tradeId => !isNil(tradeId) && tradeId === midTradeEventAction.tradeId).length > 1) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.TRADE_ID, Locale.translate(Translation.SC_DUPLICATE_TRADE_ID))
        )
      }

      if (isNil(midTradeEventAction.tradeId)) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.TRADE_ID, Locale.translate(Translation.SC_MISSING_TRADE_ID))
        )
      }

      const dealDetail = !isNil(midTradeEventAction.tradeId) ? getDealDetail(midTradeEventAction.tradeId) : null
      if (!isNil(midTradeEventAction) && !dealDetail) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.TRADE_ID, Locale.translate(Translation.SC_INVALID_TRADE_ID))
        )
      }

      if (!incoming && isNil(midTradeEventAction.actionType)) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.ACTION, Locale.translate(Translation.SC_MISSING_ACTION_TYPE))
        )
      } else if (incoming && isNil(midTradeEventAction.responseType)) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.RESPONSE, Locale.translate(Translation.SC_MISSING_RESPONSE_TYPE))
        )
      }

      const servicingType = incoming && dealDetail ? dealDetail.deal.servicingDetails?.servicingType : null

      if (
        incoming &&
        (!servicingType?.servicingType ||
          ![ServicingType.CLOSE_OPEN, ServicingType.RERATE, ServicingType.TERMINATION].includes(servicingType?.servicingType))
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingMidTradeEventFields.TRADE_ID,
            Locale.translate(Translation.SC_INVALID_SERVICING_TRADE_ID)
          )
        )
      }

      if (
        incoming &&
        servicingType?.servicingType === ServicingType.TERMINATION &&
        midTradeEventAction.responseType === MidTradeResponse.SEND
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(PendingMidTradeEventFields.RESPONSE, Locale.translate(Translation.SC_INVALID_SEND_ON_TERMINATE))
        )
      }

      const maxHaircut = userPreference.haircutSign === HaircutSign.ABSOLUTE ? 150 : 50
      const minHaircut = userPreference.haircutSign === HaircutSign.ABSOLUTE ? 50 : -50
      if (
        (midTradeEventAction.actionType === MidTradeAction.RESIZE ||
          midTradeEventAction.actionType === MidTradeAction.RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE ||
          servicingType?.servicingType === ServicingType.CLOSE_OPEN) &&
        midTradeEventAction.haircut !== null &&
        (midTradeEventAction.haircut < minHaircut || midTradeEventAction.haircut > maxHaircut)
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingMidTradeEventFields.HAIRCUT,
            Locale.translate(Translation.SC_OUT_OF_RANGE_HAIRCUT_ERROR_MESSAGE)
          )
        )
      }

      if (
        (midTradeEventAction.actionType === MidTradeAction.RESIZE || servicingType?.servicingType === ServicingType.CLOSE_OPEN) &&
        midTradeEventAction.notional !== null &&
        midTradeEventAction.notional <= 0
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingMidTradeEventFields.NOTIONAL,
            Locale.translate(Translation.SC_NEGATIVE_NOTIONAL_ERROR_MESSAGE)
          )
        )
      }

      const ticketSize = dealDetail?.collateral?.ticketIncrement || 1
      if (
        (midTradeEventAction.actionType === MidTradeAction.RESIZE || servicingType?.servicingType === ServicingType.CLOSE_OPEN) &&
        midTradeEventAction.notional !== null &&
        midTradeEventAction.tradeId !== null &&
        mod(midTradeEventAction.notional, Math.ceil(ticketSize)) !== 0
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingMidTradeEventFields.NOTIONAL,
            Locale.translate(Translation.SC_NOTIONAL_TICKET_SIZE_ERROR_MESSAGE, {
              notional: midTradeEventAction.notional,
              ticketSize
            })
          )
        )
      }

      if (
        (midTradeEventAction.actionType === MidTradeAction.RESIZE || servicingType?.servicingType === ServicingType.CLOSE_OPEN) &&
        midTradeEventAction.haircut !== null &&
        midTradeEventAction.haircutType === null
      ) {
        dataIssues.push(
          createInvalidValueValidationIssue(
            PendingMidTradeEventFields.HAIRCUT_TYPE,
            Locale.translate(Translation.SC_HAIRCUT_WITHOUT_HAIRCUT_TYPE_ERROR_MESSAGE)
          )
        )
      }

      actions[dataIssues.length === 0 ? 'validMidTradeActions' : 'invalidMidTradeActions'].push({
        ...midTradeEventAction,
        tradeId: midTradeEventAction.tradeId as string,
        dataIssues
      })

      return actions
    },
    { validMidTradeActions: [], invalidMidTradeActions: [] }
  )
}
